import React, { useState, useEffect } from 'react';
import { dunAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import './MasterDataManagement.css';

const DunManagement = () => {
  const [duns, setDuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingDun, setEditingDun] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [newDun, setNewDun] = useState({
    code: '',
    name: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    loadDuns();
  }, [searchTerm, currentPage, pageSize]);

  const loadDuns = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await dunAPI.getDuns(searchTerm);
      
      if (response.success) {
        setDuns(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      } else {
        setError(response.message || 'Failed to load DUNs');
      }
    } catch (err) {
      console.error('Load DUNs error:', err);
      setError('An error occurred while loading DUNs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDun = () => {
    setModalMode('create');
    setEditingDun(null);
    setNewDun({
      code: '',
      name: ''
    });
    setShowModal(true);
  };

  const handleEditDun = (dun) => {
    setModalMode('edit');
    setEditingDun(dun);
    setNewDun({
      code: dun.code,
      name: dun.name
    });
    setShowModal(true);
  };

  const handleDeleteDun = async (code) => {
    if (window.confirm(`Are you sure you want to delete DUN with code ${code}?`)) {
      try {
        const response = await dunAPI.deleteDun(code);
        
        if (response.success) {
          loadDuns();
          console.log('DUN deleted successfully');
        } else {
          setError(response.message || 'Failed to delete DUN');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('An error occurred while deleting the DUN');
      }
    }
  };

  const handleSaveDun = async () => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await dunAPI.createDun({
          code: newDun.code,
          name: newDun.name
        });
      } else {
        response = await dunAPI.updateDun(editingDun.code, {
          id: editingDun.id,
          code: newDun.code,
          name: newDun.name
        });
      }

      if (response.success) {
        setShowModal(false);
        loadDuns();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} DUN successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} DUN`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'creating' : 'updating'} the DUN`);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && !duns.length) {
    return (
      <div className="master-data-management">
        <div className="loading">Loading DUNs...</div>
      </div>
    );
  }

  return (
    <div className="master-data-management">
      <div className="master-data-management-header">
        <div className="header-info">
          <h2><i className="fas fa-building"></i> DUN (State Legislative Assembly) Management</h2>
          <p>Manage Dewan Undangan Negeri (State Legislative Assembly) constituencies</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search DUNs..."
            className="search-input"
          />
          <button onClick={loadDuns} className="btn-search">
            <i className="fas fa-search"></i> Search
          </button>
        </div>
        <button onClick={handleCreateDun} className="btn-create">
          <i className="fas fa-plus"></i> Add New DUN
        </button>
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>DUN Code</th>
              <th>DUN Name</th>
              <th>School Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {duns.length > 0 ? (
              duns.map((dun, index) => (
                <tr key={dun.id || dun.code}>
                  <td>{index + 1}</td>
                  <td><strong>{dun.code}</strong></td>
                  <td>{dun.name}</td>
                  <td>
                    <span className="badge badge-info">{dun.schoolCount || 0} schools</span>
                  </td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditDun(dun)}
                      className="btn-action btn-edit"
                      title="Edit DUN"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteDun(dun.code)}
                      className="btn-action btn-delete"
                      title="Delete DUN"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">No DUNs found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total DUNs)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(Number(e.target.value))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit DUN */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New DUN' : `Edit DUN: ${newDun.code}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="dunCode">DUN Code *</label>
                <input
                  id="dunCode"
                  type="text"
                  value={newDun.code}
                  onChange={(e) => setNewDun({ ...newDun, code: e.target.value.trim().toUpperCase() })}
                  placeholder="Enter DUN code (e.g., N01, N02)"
                  maxLength={50}
                  className="form-control"
                  disabled={modalMode === 'edit'} // Don't allow changing code after creation
                />
                <small className="form-help">Unique identifier for the DUN (cannot be changed after creation)</small>
              </div>

              <div className="form-group">
                <label htmlFor="dunName">DUN Name *</label>
                <input
                  id="dunName"
                  type="text"
                  value={newDun.name}
                  onChange={(e) => setNewDun({ ...newDun, name: e.target.value.trim() })}
                  placeholder="Enter full DUN name (e.g., Keningau)"
                  maxLength={255}
                  className="form-control"
                />
                <small className="form-help">Full name of the DUN constituency</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDun}
                disabled={!newDun.code.trim() || !newDun.name.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Create DUN' : 'Update DUN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DunManagement;